import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { AuthProvider } from '@/contexts/AuthContext';

// Pages
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Children from '@/pages/Children';
import Professionals from '@/pages/Professionals';
import Activities from '@/pages/Activities';
import Groups from '@/pages/Groups';
import GroupsNew from '@/pages/GroupsNew';
import Enrollments from '@/pages/Enrollments';
import Attendance from '@/pages/Attendance';
import Reports from '@/pages/Reports';
import Users from '@/pages/Users';
import Events from '@/pages/Events';
import Layout from '@/components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('scfv_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('scfv_token');
      localStorage.removeItem('scfv_user');
      window.location.href = '/login';
    }
    if (error.response?.status === 403) {
      toast.error(error.response?.data?.detail || 'Você não tem permissão para realizar esta ação');
    }
    return Promise.reject(error);
  }
);

// Helper function to check permissions
const checkPermission = (user, permission) => {
  if (!user) return false;
  if (user.is_admin) return true;
  return user.permissions?.[permission] || false;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('scfv_token');
    const savedUser = localStorage.getItem('scfv_user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  // Refresh user data from server periodically
  useEffect(() => {
    const refreshUser = async () => {
      const token = localStorage.getItem('scfv_token');
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          const updatedUser = response.data;
          localStorage.setItem('scfv_user', JSON.stringify(updatedUser));
          setUser(updatedUser);
        } catch (error) {
          // Token invalid, will be handled by interceptor
        }
      }
    };
    
    // Refresh on mount
    refreshUser();
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('scfv_token', token);
    localStorage.setItem('scfv_user', JSON.stringify(userData));
    setUser(userData);
    toast.success('Login realizado com sucesso!');
  };

  const handleLogout = () => {
    localStorage.removeItem('scfv_token');
    localStorage.removeItem('scfv_user');
    setUser(null);
    toast.info('Logout realizado');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D87A4E]"></div>
      </div>
    );
  }

  return (
    <>
      <BrowserRouter>
        <AuthProvider initialUser={user} onLogout={handleLogout}>
          <Routes>
            <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />} />
            
            {user ? (
              <Route path="/" element={<Layout user={user} onLogout={handleLogout} />}>
                <Route index element={<Dashboard />} />
                <Route path="children" element={<Children />} />
                <Route path="professionals" element={<Professionals />} />
                <Route path="activities" element={<Activities />} />
                <Route path="groups" element={<Groups />} />
                <Route path="groupsnew" element={<GroupsNew />} />
                <Route path="enrollments" element={<Enrollments />} />
                <Route path="attendance" element={<Attendance />} />
                <Route path="reports" element={<Reports />} />
                <Route path="events" element={<Events />} />
                <Route path="users" element={<Users />} />
              </Route>
            ) : (
              <Route path="*" element={<Navigate to="/login" />} />
            )}
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </>
  );
}

export default App;
export { API, checkPermission };